import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Download, Plus, Users, Clock, CheckCircle2, AlertCircle, FileText,
  FilePlus2, MessageSquare, RefreshCw, XCircle, Trash2, Pencil, Settings,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState } from '@/components/shared/empty-state'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { supabase } from '@/lib/supabase/client'
import dayjs from '@/lib/dayjs'
import { EVENT_TYPE_LABELS } from '@/features/audit/routes/audit-log-page'

interface ProjectStats {
  totalRecords: number
  pendingReview: number
  approved: number
  needsRevision: number
  activeMembers: number
}

async function fetchProjectStats(projectId: string): Promise<ProjectStats> {
  const [recordsRes, membersRes] = await Promise.all([
    supabase
      .from('records')
      .select('status')
      .eq('project_id', projectId)
      .eq('is_deleted', false),
    supabase
      .from('project_memberships')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('is_removed', false),
  ])

  const records = recordsRes.data ?? []
  return {
    totalRecords: records.length,
    pendingReview: records.filter((r) =>
      ['submitted', 'under_review'].includes(r.status)
    ).length,
    approved: records.filter((r) => r.status === 'approved').length,
    needsRevision: records.filter((r) => r.status === 'needs_revision').length,
    activeMembers: membersRes.count ?? 0,
  }
}

interface ActivityEntry {
  id: string
  event_type: string
  target_type: string
  description: string | null
  created_at: string
  record_id: string | null
  actor: { full_name: string | null } | null
}

async function fetchRecentActivity(projectId: string): Promise<ActivityEntry[]> {
  const { data, error } = await supabase
    .from('audit_entries')
    .select(`
      id, event_type, target_type, description, created_at, record_id,
      actor:user_profiles!actor_user_id(full_name)
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(8)

  if (error) throw error
  return (data ?? []) as unknown as ActivityEntry[]
}

// Maps an event_type to its icon + accent color for the activity feed.
function activityVisual(eventType: string): { icon: React.ElementType; className: string } {
  if (eventType.startsWith('record_created')) return { icon: FilePlus2, className: 'bg-primary/15 text-primary' }
  if (eventType.includes('approved')) return { icon: CheckCircle2, className: 'bg-secondary/15 text-secondary' }
  if (eventType.includes('declined') || eventType.includes('needs_revision')) return { icon: XCircle, className: 'bg-destructive/15 text-red-400' }
  if (eventType.includes('deleted')) return { icon: Trash2, className: 'bg-destructive/15 text-red-400' }
  if (eventType.startsWith('comment')) return { icon: MessageSquare, className: 'bg-purple-500/15 text-purple-400' }
  if (eventType.startsWith('form_') || eventType.startsWith('submission') || eventType.includes('resubmit')) return { icon: RefreshCw, className: 'bg-blue-500/15 text-blue-400' }
  if (eventType.startsWith('member') || eventType.startsWith('project')) return { icon: Settings, className: 'bg-yellow-500/15 text-yellow-400' }
  return { icon: Pencil, className: 'bg-muted text-muted-foreground' }
}

interface StatCardProps {
  label: string
  value: number | string
  icon: React.ElementType
  iconClassName?: string
  loading?: boolean
}

function StatCard({ label, value, icon: Icon, iconClassName, loading }: StatCardProps) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', iconClassName ?? 'bg-primary/15')}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      {loading ? (
        <div className="h-8 bg-muted rounded animate-pulse w-20" />
      ) : (
        <p className="text-3xl font-bold text-foreground font-mono">
          {typeof value === 'number' ? value.toLocaleString('fa-IR') : value}
        </p>
      )}
    </div>
  )
}

export function OverviewPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: stats, isLoading } = useQuery({
    queryKey: ['project-stats', projectId],
    queryFn: () => fetchProjectStats(projectId!),
    enabled: !!projectId,
  })

  const { data: activity = [], isLoading: activityLoading } = useQuery({
    queryKey: ['project-activity', projectId],
    queryFn: () => fetchRecentActivity(projectId!),
    enabled: !!projectId,
  })

  const pageActions = (
    <>
      <Button variant="outline" size="sm" className="gap-1.5">
        <Download className="w-4 h-4" />
        {t('overview.export', 'خروجی')}
      </Button>
      <Button size="sm" className="gap-1.5">
        <Plus className="w-4 h-4" />
        {t('overview.newRecord', 'رکورد جدید')}
      </Button>
    </>
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={t('overview.title', 'نمای کلی پروژه')}
        breadcrumbs={[
          { label: t('nav.projects', 'پروژه‌ها'), href: '/projects' },
          { label: t('nav.overview', 'نمای کلی') },
        ]}
        actions={pageActions}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label={t('overview.stats.totalRecords', 'کل سوابق')}
            value={stats?.totalRecords ?? 0}
            icon={FileText}
            iconClassName="bg-primary/15 text-primary"
            loading={isLoading}
          />
          <StatCard
            label={t('overview.stats.pendingReview', 'در انتظار بررسی')}
            value={stats?.pendingReview ?? 0}
            icon={Clock}
            iconClassName="bg-yellow-500/15 text-yellow-400"
            loading={isLoading}
          />
          <StatCard
            label={t('overview.stats.approved', 'تأیید شده')}
            value={stats?.approved ?? 0}
            icon={CheckCircle2}
            iconClassName="bg-secondary/15 text-secondary"
            loading={isLoading}
          />
          <StatCard
            label={t('overview.stats.needsRevision', 'نیاز به اصلاح')}
            value={stats?.needsRevision ?? 0}
            icon={AlertCircle}
            iconClassName="bg-destructive/15 text-red-400"
            loading={isLoading}
          />
          <StatCard
            label={t('overview.stats.activeMembers', 'اعضای فعال')}
            value={stats?.activeMembers ?? 0}
            icon={Users}
            iconClassName="bg-purple-500/15 text-purple-400"
            loading={isLoading}
          />
        </div>

        {/* Main content area */}
        {!isLoading && stats?.totalRecords === 0 ? (
          <EmptyState
            icon={FileText}
            title={t('overview.noRecords', 'هنوز هیچ سابقه‌ای ثبت نشده')}
            description={t(
              'overview.noRecordsDesc',
              'اولین سابقه پزشکی را برای این پروژه ثبت کنید'
            )}
            action={
              <Button size="sm" className="gap-1.5">
                <Plus className="w-4 h-4" />
                {t('overview.newRecord', 'رکورد جدید')}
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recent activity — 2/3 width */}
            <div className="lg:col-span-2 bg-card border border-border rounded-xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground">
                  {t('overview.recentActivity', 'فعالیت اخیر')}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => navigate(`/projects/${projectId}/audit`)}
                >
                  {t('overview.viewAll', 'مشاهده همه')}
                </Button>
              </div>
              {activityLoading ? (
                <div className="p-5 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="w-7 h-7 rounded-full bg-muted shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <div className="p-5">
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('overview.noActivity', 'فعالیتی برای نمایش وجود ندارد')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {activity.map((entry) => {
                    const { icon: Icon, className } = activityVisual(entry.event_type)
                    const label = EVENT_TYPE_LABELS[entry.event_type] ?? entry.event_type
                    const actorName = entry.actor?.full_name ?? t('overview.systemActor', 'سیستم')
                    const row = (
                      <div className="flex gap-3 px-5 py-3 hover:bg-muted/20 transition-colors">
                        <div className={cn('w-7 h-7 rounded-full flex items-center justify-center shrink-0', className)}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">
                            <span className="font-medium">{actorName}</span>
                            <span className="text-muted-foreground"> · {label}</span>
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {entry.description ?? '—'}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          {dayjs(entry.created_at).fromNow()}
                        </span>
                      </div>
                    )
                    return (
                      <li key={entry.id}>
                        {entry.record_id ? (
                          <Link to={`/projects/${projectId}/records/${entry.record_id}`} className="block">
                            {row}
                          </Link>
                        ) : (
                          row
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Right column — 1/3 width */}
            <div className="space-y-4">
              {/* Pending queue CTA */}
              {(stats?.pendingReview ?? 0) > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/25 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-sm font-semibold text-foreground">
                      {t('overview.pendingQueue', 'صف بررسی')}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {(stats?.pendingReview ?? 0).toLocaleString('fa-IR')} سابقه در انتظار بررسی
                  </p>
                  <Button size="sm" variant="outline" className="w-full text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10">
                    {t('overview.reviewNow', 'بررسی اکنون')}
                  </Button>
                </div>
              )}

              {/* Project health card */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  {t('overview.projectHealth', 'سلامت پروژه')}
                </h3>
                {isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-4 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <HealthBar
                      label={t('status.approved', 'تأیید شده')}
                      count={stats?.approved ?? 0}
                      total={stats?.totalRecords ?? 1}
                      colorClass="bg-secondary"
                    />
                    <HealthBar
                      label={t('status.in_progress', 'در جریان')}
                      count={(stats?.totalRecords ?? 0) - (stats?.approved ?? 0) - (stats?.needsRevision ?? 0) - (stats?.pendingReview ?? 0)}
                      total={stats?.totalRecords ?? 1}
                      colorClass="bg-primary"
                    />
                    <HealthBar
                      label={t('status.needs_revision', 'نیاز به اصلاح')}
                      count={stats?.needsRevision ?? 0}
                      total={stats?.totalRecords ?? 1}
                      colorClass="bg-destructive"
                    />
                  </div>
                )}
              </div>

              {/* Project metadata card */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  {t('overview.projectInfo', 'اطلاعات پروژه')}
                </h3>
                <div className="space-y-2">
                  <MetaRow
                    label={t('overview.projectId', 'شناسه پروژه')}
                    value={projectId ?? '—'}
                    mono
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function HealthBar({
  label,
  count,
  total,
  colorClass,
}: {
  label: string
  count: number
  total: number
  colorClass: string
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground font-mono">{pct}٪</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full', colorClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function MetaRow({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          'text-xs text-foreground',
          mono && 'font-mono'
        )}
      >
        {value}
      </span>
    </div>
  )
}
